import { useMemo } from 'react';
import type { FC } from 'react';
import { Box, Flex } from '@chakra-ui/react';
import isUndefined from 'lodash-es/isUndefined';
import { useEffectiveDateTime } from '../../bff-api/customers/useEffectiveDateTime';
import { useIsMobile } from '../../hooks/use-is-mobile';
import { Badge } from '../../ui-components/badge';
import { checkBadgeVisibility } from '../../utils/badge-visibility';
import { CarouselView } from './carousel-view/CarouselView';
import { SingleAssetView } from './single-asset-view/SingleAssetView';
import { TwoColumnView } from './two-column-view/TwoColumnView';
import { PhotoGridStoreProvider } from './usePhotoGridProductId';
import { usePhotoGridStore } from './usePhotoStore';
import { Zoom } from './zoom/Zoom';
import type { Badge as BadgeType } from '../../bff-api/products/model/Badge';
import type { AssetInfo } from './utils';

interface PhotoGridProps {
    badge?: BadgeType;
    thumbnailDisabled?: boolean;
}

const PhotoGridLayout: FC<PhotoGridProps> = ({ badge, thumbnailDisabled = false }) => {
    const { isLoading, data } = usePhotoGridStore();
    const effectiveDateTime = useEffectiveDateTime();
    const isBadgeVisible = useMemo(
        () =>
            checkBadgeVisibility({
                badgeEndDate: badge?.badgeEndDate,
                badgeStartDate: badge?.badgeStartDate,
                badgeLabel: badge?.badgeLabel,
                badgeShowOnPDP: badge?.badgeShowOnPDP,
                badgeShowOnPLP: badge?.badgeShowOnPLP,
                pageType: 'PDP',
                effectiveDateTime,
            }),
        [badge, effectiveDateTime],
    );

    const { assets, isZoomModalOpen } = data ?? { assets: [], isZoomModalOpen: false };

    const showLoadingOverlay = isLoading && !isZoomModalOpen;
    return (
        <>
            <Box className="flex w-full" data-testid="photo-grid" position="relative">
                {isBadgeVisible && (
                    <Box position="absolute" top={4} left={{ base: 4, sm: 8, md: 4 }} zIndex="5">
                        <Badge text={badge?.badgeLabel || ''} />
                    </Box>
                )}
                {assets?.length === 1 ? (
                    <SingleAssetView asset={assets[0]} />
                ) : (
                    <ResponsiveMultipleAssetPhotoGrid assets={assets || []} />
                )}
                {showLoadingOverlay ? (
                    <Flex
                        position="absolute"
                        inset={0}
                        backgroundColor="white"
                        opacity="0.7"
                        zIndex="2"
                    />
                ) : null}
            </Box>
            <Zoom thumbnailDisabled={thumbnailDisabled} />
        </>
    );
};

const ResponsiveMultipleAssetPhotoGrid: FC<{ assets: AssetInfo[] }> = ({ assets }) => {
    const isMobile = useIsMobile();

    if (isUndefined(isMobile)) {
        return (
            <>
                <Box w="100%" h="fit-content" hideFrom="md">
                    <CarouselView assets={assets || []} />
                </Box>
                <Box w="100%" h="fit-content" hideBelow="md">
                    <TwoColumnView assets={assets || []} />
                </Box>
            </>
        );
    }

    return (
        <Box w="100%" h="fit-content">
            {isMobile ? <CarouselView assets={assets} /> : <TwoColumnView assets={assets} />}
        </Box>
    );
};

export const PhotoGrid: FC<PhotoGridProps & { productId: string }> = ({ productId, ...props }) => (
    <PhotoGridStoreProvider productId={productId}>
        <PhotoGridLayout {...props} />
    </PhotoGridStoreProvider>
);
